import type {
  FloorplanDto,
  GetListingByAddressListingFilterInputDto,
  GetListingByAddressRequestDto,
  GetListingByAddressResponseDto,
} from '../../types/RentalGrowthApiTypes';

import api from '../../utils/api';
import { createMarketingLead, createMarketUpdate } from '../actions/MarketingActions';
import pathsManifest from '../../utils/pathsManifest';
import { encodeFullAddressForUrl } from '../../utils/urlUtils';
import routingUtils from '../../utils/routingUtils';
import { augmentError } from '../../utils/errors';
import errorConstants from '../../constants/ErrorConstants';

import AutocompleteAddress from '../../models/AutocompleteAddress';
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';
import AddressDetails from '../../models/AddressDetails';

import { emitRentZestimateEvent } from '../../analytics/gtmEvents';
import { emitErrorEvent, emitSuccessEvent } from '../../analytics/priceMyRentalEvents';

import type { APIResult, AutocompleteAddressApiType, getListingForAddressApiType } from '../../types/ApiResponseTypes';
import type { FilterParamsType, QueryDataObjectType } from '../../types/QueryDataObjectType';
import type { UserProps } from '../../models/User';
import type { State } from '../../types/State';

const ADDRESS_STATUS_FAILURE = 'FAILURE';

export type AugmentedAutocompleteAddressType = AutocompleteAddress & {
  isRecent: boolean;
  unit: string;
};

const throwNonCriticalError = ({ message, code }: { message: string; code: string }): never => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};

const buildSimilarFloorplansFilter = (params: FilterParamsType): GetListingByAddressListingFilterInputDto => {
  const {
    activeTypes,
    activatedDays,
    amenities,
    bedrooms,
    deactivatedDays,
    distanceInMiles,
    laundry,
    limit,
    offset,
    orderBy,
    pets,
    price,
    propertyTypes,
  } = params;

  return {
    orderBy: orderBy || 'lowPrice',
    ...(activeTypes && { activeTypes }),
    ...(activatedDays && { activatedDays }),
    ...(amenities && { amenities }),
    ...(bedrooms && { bedrooms }),
    ...(deactivatedDays && { deactivatedDays }),
    ...(distanceInMiles && { distanceInMiles }),
    ...(laundry && { laundry }),
    ...(limit && { limit }),
    ...(offset && { offset }),
    ...(pets && { pets }),
    ...(price && { price }),
    ...(propertyTypes && { propertyTypes }),
  };
};

// ActionCreators... <verb><Noun>
export const goToAddressItem = (
  addressDataObject: AugmentedAutocompleteAddressType,
  user: UserProps,
  urlQueryObject: Record<string, string>,
  addRecentSearch: (arg: AugmentedAutocompleteAddressType) => void,
  setAddress: (arg: Address) => void,
  address: Address,
) => {
  return async () => {
    try {
      const userEmail = user.email;

      addRecentSearch(addressDataObject);

      // Fire-and-forget: invoke the thunk but don't block the flow on the result.
      void createMarketingLead({
        fullAddress: addressDataObject.name || '',
        unit: addressDataObject.unit,
        email: userEmail,
        urlQueryObject,
      })();

      const { userAddress } = await getAddressAndComparables(addressDataObject, user, setAddress, address)();

      void createMarketUpdate({
        email: userEmail,
        zpid: userAddress.zpid,
        price: userAddress.rentZestimate,
        lat: userAddress.lat,
        lon: userAddress.lon,
      })();

      const encodedFullAddress = encodeFullAddressForUrl({
        address: addressDataObject.name,
        unit: addressDataObject.unit,
      });

      emitSuccessEvent({ user });
      emitRentZestimateEvent();

      return routingUtils.push(`${pathsManifest.PriceMyRentalPageResults}/${encodedFullAddress}`);
    } catch (err) {
      const error = err as { data?: { action?: string } };
      const shouldLogAction = !error.data || !error.data.action;

      augmentError(err as Error, {
        ...(shouldLogAction && {
          action: 'Error in AddressActions#goToAddressItem',
        }),
      });

      throw err;
    }
  };
};

export const getComparables = (params: QueryDataObjectType, user: UserProps, address: Address) => {
  return async () => {
    const { street, city, state, zip, unit, ...filterParams } = params;
    const requestDto: GetListingByAddressRequestDto = {
      street: street ?? '',
      city: city ?? '',
      state: state ?? '',
      zip: zip ?? '',
      ...(unit && { unit }),
      similarFloorplansFilter: buildSimilarFloorplansFilter(filterParams),
    };
    const { data } = await fetchListingByAddress(requestDto, user)();
    const userComps = setComparables(data, user, address);

    return { userComps };
  };
};

export const getAddressAndComparables = (
  params: AugmentedAutocompleteAddressType,
  user: UserProps,
  setAddress: (arg: Address) => void,
  address: Address,
) => {
  return async () => {
    const {
      street,
      city,
      state,
      zip,
      unit,
      name: _name,
      propertyId: _propertyId,
      isRecent: _isRecent,
      ...filterParams
    } = params;
    const requestDto: GetListingByAddressRequestDto = {
      street: street ?? '',
      city: city ?? '',
      state: state ?? '',
      zip: zip ?? '',
      ...(unit && { unit }),
      similarFloorplansFilter: buildSimilarFloorplansFilter(filterParams as FilterParamsType),
    };
    const { data } = await fetchListingByAddress(requestDto, user)();
    const userAddress = await resolveAddress(data, user, setAddress)();
    const hasZestimate = Boolean(data.floorplans?.[0]?.zestimate?.rentZestimate);
    const hasCompsPrices = Boolean(data.similarFloorplans?.floorplans?.length);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      setComparables(data, user, address);
    }

    return {
      userAddress,
    };
  };
};

// API fetches
export const fetchAutocompleteData = (partialAddress: string) => {
  return async () => {
    try {
      const data = (await api.address.autocomplete(partialAddress)) as { addresses: Array<AutocompleteAddressApiType> };
      return data.addresses.map((address) => new AutocompleteAddress(address));
    } catch (error) {
      augmentError(error as Error, {
        action: 'Error in AddressActions#fetchAutocompleteData',
      });

      throw error;
    }
  };
};

export const fetchListingByAddress = (requestDto: GetListingByAddressRequestDto, user: UserProps) => {
  return async () => {
    try {
      const response = await api.listing.getByAddress(requestDto);

      if (!response.floorplans?.length) {
        emitErrorEvent({ user });
        throwNonCriticalError({
          message: 'We were unable to match this address',
          code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
        });
      }
      if (response.similarFloorplansError || !response.similarFloorplans?.floorplans?.length) {
        emitErrorEvent({ user });
      }
      if (!response.floorplans?.[0]?.zestimate?.rentZestimate) {
        emitErrorEvent({ user });
      }

      return { data: response };
    } catch (error) {
      augmentError(error as Error, {
        action: 'Error in AddressActions#fetchListingByAddress',
      });

      throw error;
    }
  };
};

const resolveAddress = (data: GetListingByAddressResponseDto, user: UserProps, setAddress: (arg: Address) => void) => {
  return async () => {
    const addressData: FloorplanDto & {
      addressHasListing?: boolean;
      zrmListingId?: string;
      marketSummary?: GetListingByAddressResponseDto['marketSummary'];
    } = {
      ...data.floorplans![0],
      geo: data.geo,
      marketSummary: data.marketSummary,
    };

    if (user.loggedIn) {
      const { street, city, state, zip, unit } = addressData.address;
      const fullAddress = `${street},${city}, ${state} ${zip}`;

      try {
        const res = (await api.address.getListingForAddress(fullAddress, unit)) as getListingForAddressApiType;
        addressData.addressHasListing = res.listingFound;
        if (res.listingFound) {
          addressData.zrmListingId = res.listing.listingId;
        }
      } catch (error) {
        augmentError(error as Error, {
          action: 'Error in AddressActions#resolveAddress',
        });
        throw error;
      }
    }

    const modeledAddressData = new Address(addressData);
    setAddress(modeledAddressData);
    return modeledAddressData;
  };
};

export const setComparables = (data: GetListingByAddressResponseDto, user: UserProps, address: Address) => {
  const similarFloorplans = data.similarFloorplans;

  if (!similarFloorplans?.floorplans) {
    emitErrorEvent({ user });
    return [];
  }

  const { floorplans, listingRanking } = similarFloorplans;

  if (floorplans.length === 0) {
    emitErrorEvent({ user });
  }

  const compsItems = floorplans.map((comp: FloorplanDto) => new Comparables(comp));
  const userProperty = address as unknown as State['address'];
  compsItems.splice(listingRanking, 0, userProperty as unknown as Comparables);

  return compsItems;
};

type RestrictionsParams = {
  address: string;
  unit: string;
  listingTypeCode: number;
  listingAlias?: string;
  propertyTypeCode: number | null;
};

const getRestrictionsImpl = (params: RestrictionsParams, isPublic: boolean) => {
  return async () => {
    const fn = isPublic ? api.properties.locations.getPublicRestrictions : api.properties.locations.getRestrictions;
    const response = (await fn(params)) as APIResult;
    if (response.success && response.response) {
      return response.response;
    }
    throw response.error;
  };
};

const AddressActions = {
  getDetails(
    addressData: { address: string; unit: string },
    setAddressDetails: (arg: ReturnType<typeof AddressDetails>) => void,
  ) {
    return async () => {
      const result = (await api.properties.address.details(addressData)) as APIResult;
      if (!result.success || !result.response) {
        throw result.error;
      }

      const getDetailsResponse = result.response as { status?: string; message?: string } & Parameters<
        typeof AddressDetails
      >[0];
      const { status, message } = getDetailsResponse;
      if (status && status.toLowerCase() === ADDRESS_STATUS_FAILURE.toLowerCase()) {
        throw message;
      }

      const addressDetailsModel = AddressDetails(getDetailsResponse);
      setAddressDetails(addressDetailsModel);
      return addressDetailsModel;
    };
  },
  verify(addressData: { address: string; unit: string; propertyTypeCode?: number }) {
    return async () => {
      const response = (await api.properties.address.verification({
        address: addressData.address,
        unit: addressData.unit,
        propertyTypeCode: addressData.propertyTypeCode,
      })) as APIResult;
      if (response.success && response.response) {
        return response.response;
      }
      throw response.error;
    };
  },
  getRestrictions(addressData: RestrictionsParams) {
    return getRestrictionsImpl(addressData, false);
  },
  getPublicRestrictions(addressData: RestrictionsParams) {
    return getRestrictionsImpl(addressData, true);
  },
};

export default AddressActions;
